// services/package.service.ts
import axiosInstance from './axios.config';
import { BRAND_NAME } from '../config/env';

export interface SaasPackage {
  id: string;
  nom: string;
  description?: string;
  modules: string[];
  couleur?: string;
  icone?: string;
  prix?: number;
  devise?: string;  // XOF, GNF, EUR, USD, MAD...
  pays?: string;    // CI, GN, FR, SN, ML, BF, TG, BJ, NE...
  statut: 'actif' | 'inactif';
  createdAt?: string;
  updatedAt?: string;
}

export interface CreatePackageDto {
  nom: string;
  description?: string;
  modules: string[];
  couleur?: string;
  icone?: string;
  prix?: number;
  devise?: string;
  pays?: string;
  statut?: 'actif' | 'inactif';
}

// Libellés pays disponibles
export const PAYS_LABELS: Record<string, string> = {
  BJ: 'Bénin',
  BF: 'Burkina Faso',
  CA: 'Canada',
  CM: 'Cameroun',
  CG: 'Congo',
  CI: 'Côte d\'Ivoire',
  FR: 'France',
  GA: 'Gabon',
  GN: 'Guinée Conakry',
  MA: 'Maroc',
  ML: 'Mali',
  NE: 'Niger',
  SN: 'Sénégal',
  TG: 'Togo',
};

export type UpdatePackageDto = Partial<CreatePackageDto>;

/**
 * @deprecated Liste fallback uniquement.
 * La source de vérité est désormais l'API `/api/modules-catalogue`.
 * Voir `getModulesCatalogue()` ci-dessous et le composant `MODULES_DISPONIBLES`
 * dans Administration.tsx (chargement async + fallback).
 */
export const MODULES_DISPONIBLES: {
  key: string;
  label: string;
  description: string;
  /** Module fourni par un service externe (ex: SIGP) — pas une route SAGeCom interne. */
  external?: boolean;
  /** URL d'accès au produit externe (ouvre dans un nouvel onglet). */
  externalUrl?: string;
}[] = [
  { key: 'caisse', label: 'Caisse & Stock', description: 'Gestion de caisse, stock, inventaire, vente directe' },
  { key: 'ventes_stock', label: 'Ventes & Stock avancé', description: 'Ventes avancées, gestion stock étendue' },
  { key: 'clients', label: 'Clients & Factures', description: 'Gestion clients, facturation' },
  { key: 'promotions', label: 'Promotions', description: 'Codes promo, cartes cadeaux, bons de réduction' },
  { key: 'achats', label: 'Achats', description: 'Commandes fournisseurs, réceptions, gestion des achats' },
  { key: 'decaissement_workflow', label: 'Décaissement Workflow', description: 'Demandes de décaissement avec validation' },
  { key: 'rh', label: 'Ressources Humaines', description: 'Employés, contrats, paie, congés, recrutement' },
  // MM-64 — Séparation Statistiques / Comptabilité (cf. backend module-catalogue.service.ts)
  { key: 'statistiques', label: 'Statistiques & Reporting', description: 'Tableau de bord, statistiques de vente, bilan périodique, exports' },
  { key: 'comptabilite', label: 'Comptabilité OHADA', description: 'Plan comptable SYSCOHADA, écritures, journaux, schémas comptables' },
  { key: 'finances', label: 'Finances', description: 'Budgets, plan trésorerie, recouvrement, rapprochement bancaire, KPIs, SIG SYSCOHADA, workflow validation budget' },
  { key: 'immobilier', label: 'Vente Immobilière', description: 'Biens, programmes, devis, factures, échéances de paiement' },
  { key: 'supervision', label: 'Supervision', description: 'Supervision des opérations en temps réel' },
  { key: 'vie_scolaire', label: 'Vie Scolaire', description: 'Gestion des écoles, classes, notes, diplômes et cérémonies de remise' },
  { key: 'espace_client', label: 'Espace Client', description: 'Portail public de paiement en ligne des factures (eau, électricité, internet, scolarité, loyer…) — Mobile Money, carte, virement' },
  // ── Modules du package Patronat / Association ─────────────────────────
  { key: 'courrier', label: 'Courrier (GEC)', description: 'Gestion électronique des courriers entrants et sortants (workflow validation+imputation SG, traitement multi-services, signature, archivage)' },
  { key: 'gestion_adherents', label: 'Gestion des Adhérents', description: 'Gestion des entreprises/personnes adhérentes (cotisations, attestations, communications, annuaire)' },
  { key: 'gestion_activites', label: 'Gestion des Activités', description: 'Gestion des activités, événements, formations et ateliers de l\'organisation' },
  { key: 'secretariat_general', label: 'Secrétariat général', description: 'Outils du secrétariat général : agenda, réunions, comptes-rendus, suivi des décisions' },
  { key: 'mon_espace', label: 'Mon Espace', description: 'Espace personnel : tâches assignées, courriers en cours, historique d\'actions, notifications' },
  // ── Pack BTP & Construction (sprint 3) ─────────────────────────────────
  { key: 'btp_chantier', label: 'Chantiers BTP', description: 'Suivi de chantier BTP : budget prévu vs réel, rentabilité temps réel, matériaux, paie ouvriers, sous-traitance, alertes budget automatiques.' },
  { key: 'btp_devis', label: 'Devis BTP', description: 'Devis chantier avec lignes, échéancier 30/40/30, retenue de garantie 5 % (compte 4191 SYSCOHADA), génération automatique de factures progressives.' },
  // ── Projets génériques ────────────────────────────────────────────────
  { key: 'projets', label: 'Gestion des projets', description: 'Gestion générique de projets : études sectorielles, événements, formations, partenariats, missions, R&D, opérationnel — avec jalons, livrables, équipe et budget.' },

  // ── Facturation Électricité (EEC) ─────────────────────────────────────
  { key: 'electricite', label: 'Facturation Électricité', description: 'Facturation électrique EEC : tarifs, campagnes & cycles de relève, lots, relevés terrain (application mobile), calcul BT/MT, factures (vérification, émission, validation, PDF), distribution et bilan. Postpayé et prépayé.' },

  // ── Dossier Électronique (GED) ───────────────────────────────────────
  { key: 'dossier_electronique', label: 'Dossier Électronique', description: 'GED transversale : archivage automatique de tous les PDFs générés, dépôt manuel, plan de classement arborescent par module, recherche multicritère.' },
  // ── Août 2026 — Modules entrés au catalogue backend (cf. module-catalogue.service.ts).
  //    Sans ces entrées, la landing page et le tunnel de souscription affichaient
  //    la clé technique brute au lieu du libellé commercial.
  { key: 'consolidation_groupe', label: 'Consolidation Groupe', description: 'Consolidation statutaire multi-entités : périmètre, méthodes, agrégation, retraitements et éliminations intra-groupe, états consolidés SYSCOHADA. Inclut le volet Consolidation Électricité.' },
  { key: 'agregateur_paiement', label: 'Agrégateur de paiement', description: 'Orchestration de paiements multi-marchands au-dessus de CinetPay : marchands et clés API, liens de paiement et QR, tarification commission/forfait/hybride, relevés mensuels et checkout public.' },
  { key: 'fonds_epargne', label: "Fonds d'épargne", description: "Back-office d'un fonds d'épargne à adhérents : registre des souscripteurs avec KYC à double regard, versements, rapprochement bancaire, capitalisation périodique, attestations et relevés PDF." },
  { key: 'edition', label: 'Édition', description: "Moteur d'édition dynamique : choix de la source de données, filtres et colonnes paramétrables, aperçu, génération PDF / Excel / CSV et modèles enregistrés réutilisables." },
  { key: 'mode_couture', label: 'Mode Couture', description: "Estimation des mesures d'un client à partir de photos (IA vision), profils de mesures, validation par le couturier, self-service client sur le portail public et paiement à l'usage." },
  // ── SIGP (Système Intégré de Gestion des Procédures) — produit externe ─
  {
    key: 'sigp',
    label: 'SIGP — Cabinets juridiques',
    description: `Plateforme juridique externe (avocats / huissiers / notaires). Connectée à ${BRAND_NAME} pour la facturation, la comptabilité, la caisse et l'espace client.`,
    external: true,
    externalUrl: 'https://sigp.sag.ci/',
  },
];

export interface ModuleCatalogueDB {
  id: string;
  cle: string;
  libelle: string;
  description?: string;
  icone?: string;
  couleur?: string;
  ordre: number;
  actif: boolean;
  premium: boolean;
  categoriesDroits: string[];
  beta: boolean;
  /** Route racine pour le sidebar auto (ex: '/adherents') */
  routeBase?: string | null;
  /** Si true, le module apparaît automatiquement dans le sidebar */
  autoSidebar?: boolean;
  /** Sous-liens statiques affichés dans le sidebar sous "Vue d'ensemble" */
  liens?: Array<{ label: string; icone?: string; path: string; permission?: string }>;
  /** Mai 2026 — Module fourni par un service externe (ex: SIGP). */
  external?: boolean;
  /** Mai 2026 — URL d'accès au produit externe (nouvel onglet). */
  externalUrl?: string | null;
  /** Bloc sidebar cible : 'erp' (défaut) ou 'crm' */
  superBloc?: string | null;
}

/**
 * Charge la liste FULL des modules (avec métadonnées sidebar) depuis l'API.
 * Utilisé par le sidebar pour générer dynamiquement les items des modules
 * marqués `autoSidebar = true`.
 */
export async function getModulesCatalogueFull(): Promise<ModuleCatalogueDB[]> {
  try {
    const axios = (await import('axios')).default;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1920/api';
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    const r = await axios.get(`${API_URL}/modules-catalogue`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      params: { actifsUniquement: 'true' },
    });
    return (r.data || []) as ModuleCatalogueDB[];
  } catch {
    return [];
  }
}

/**
 * Charge la liste des modules disponibles depuis l'API (source de vérité = BD).
 * Retourne `MODULES_DISPONIBLES` (fallback statique) si l'API est inaccessible.
 */
export async function getModulesCatalogue(): Promise<typeof MODULES_DISPONIBLES> {
  try {
    const axios = (await import('axios')).default;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1920/api';
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    const r = await axios.get(`${API_URL}/modules-catalogue`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      params: { actifsUniquement: 'true' },
    });
    const data: ModuleCatalogueDB[] = r.data || [];
    if (!data.length) return MODULES_DISPONIBLES;
    return data.map(m => ({
      key: m.cle,
      label: m.libelle,
      description: m.description || '',
      external: m.external ?? false,
      externalUrl: m.externalUrl ?? undefined,
    }));
  } catch {
    return MODULES_DISPONIBLES;
  }
}

const BASE = '/packages';

const packageService = {
  getAll: (): Promise<SaasPackage[]> =>
    axiosInstance.get(BASE).then(r => r.data),

  getById: (id: string): Promise<SaasPackage> =>
    axiosInstance.get(`${BASE}/${id}`).then(r => r.data),

  create: (data: CreatePackageDto): Promise<SaasPackage> =>
    axiosInstance.post(BASE, data).then(r => r.data),

  update: (id: string, data: UpdatePackageDto): Promise<SaasPackage> =>
    axiosInstance.put(`${BASE}/${id}`, data).then(r => r.data),

  delete: (id: string): Promise<void> =>
    axiosInstance.delete(`${BASE}/${id}`).then(r => r.data),

  seedDefaults: (): Promise<SaasPackage[]> =>
    axiosInstance.post(`${BASE}/seed`).then(r => r.data),
};

export default packageService;
